/**
 * TanStack Query hooks for Articles Analysis
 *
 * This file provides React Query hooks for fetching and mutating article analysis data.
 * These hooks replace the async methods in the Zustand articles-analysis store.
 *
 * Note: Most analysis data is populated via the articles queries (getById).
 * This file primarily handles updates and provides cache utilities for semantic terms.
 *
 * @module articles-analysis-queries
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  type ContentAnalysis,
  type ContentAnalysisSemanticTerm,
  SemanticTermStatus,
} from '../../entity/api/content-analysis';
import { retryAnalysisByArticleId, updateArticleAnalysisById } from '../../sdk/article-analysis';

// ============ TYPES ============

export type SemanticTermsFilters = {
  inHeadings: boolean;
  unused: boolean;
  overused: boolean;
  passed: boolean;
  needsImprovement: boolean;
};

// ============ QUERY KEYS ============

export const articlesAnalysisKeys = {
  all: ['articles-analysis'] as const,
  byArticleId: function (articleId: string) {
    return [...articlesAnalysisKeys.all, articleId] as const;
  },
};

// ============ FILTER HELPERS ============

function includeByVisibility(term: ContentAnalysisSemanticTerm, isHidden: boolean): boolean {
  if (isHidden) {
    return term.isHidden === true;
  }
  return term.isHidden !== true;
}

function includeBySearchTerm(
  term: ContentAnalysisSemanticTerm,
  searchTerm: string | null | undefined
): boolean {
  if (searchTerm == null || searchTerm.trim().length === 0) {
    return true;
  }
  return term.term.includes(searchTerm);
}

export function includeByFilter(
  term: ContentAnalysisSemanticTerm,
  filters: SemanticTermsFilters | null | undefined
): boolean {
  if (filters == null) {
    return true;
  }
  const { passed, unused, needsImprovement, inHeadings } = filters;
  if (!passed && !unused && !needsImprovement && !inHeadings) {
    return true;
  }
  const matchesStatus =
    (passed && term.status === SemanticTermStatus.Passed) ||
    (unused && term.status === SemanticTermStatus.Unused) ||
    (needsImprovement && term.status === SemanticTermStatus.NeedsImprovement);

  if (inHeadings) {
    if (!passed && !unused && !needsImprovement) {
      return term.usedInHeadings;
    }
    return matchesStatus && term.usedInHeadings;
  }
  return matchesStatus;
}

// ============ QUERIES ============

/**
 * Get article analysis by article ID from cache
 * Note: Analysis data is typically populated via useArticleById query.
 * This hook reads from cache and doesn't fetch directly.
 *
 * Replaces: getStores().articlesAnalysis((s) => s.getByArticleIdFromStore(articleId))
 */
export function useArticleAnalysisByArticleId(articleId: string | undefined | null) {
  return useQuery<ContentAnalysis | undefined>({
    queryKey: articlesAnalysisKeys.byArticleId(articleId ?? ''),
    queryFn: function (): ContentAnalysis | undefined {
      // This query relies on data being populated by useArticleById
      // Return undefined if not in cache - the parent article query should populate this
      return undefined;
    },
    enabled: articleId != null && articleId !== '',
    // Cache-only reader: data is populated by useArticleById, never refetch
    staleTime: Infinity,
  });
}

// ============ MUTATIONS ============

/**
 * Update article analysis by article ID
 *
 * Replaces: getStores().articlesAnalysis.getState().updateByArticleId(articleId, data)
 */
export function useUpdateArticleAnalysis() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({
      articleId,
      data,
    }: {
      articleId: string;
      data?: Partial<ContentAnalysis>;
    }) {
      // Get existing analysis from cache
      const existingAnalysis = queryClient.getQueryData<ContentAnalysis>(
        articlesAnalysisKeys.byArticleId(articleId)
      );

      if (existingAnalysis == null) {
        throw new Error('Article analysis not found');
      }

      const newAnalysis: ContentAnalysis = {
        ...existingAnalysis,
        ...(data ?? {}),
      };

      await updateArticleAnalysisById(articleId, newAnalysis);
      return newAnalysis;
    },
    onSuccess: function (newAnalysis, { articleId }) {
      // Update the cache
      queryClient.setQueryData(articlesAnalysisKeys.byArticleId(articleId), newAnalysis);
    },
  });
}

/**
 * Retry article analysis
 *
 * Replaces: getStores().articlesAnalysis.getState().retryByArticleId(articleId)
 */
export function useRetryArticleAnalysis() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function (articleId: string) {
      await retryAnalysisByArticleId(articleId);
      return articleId;
    },
    onSuccess: function (articleId) {
      // Invalidate to refetch when ready
      void queryClient.invalidateQueries({
        queryKey: articlesAnalysisKeys.byArticleId(articleId),
      });
    },
  });
}

// ============ CACHE HELPERS ============

/**
 * Get article analysis from cache by article ID
 */
export function getArticleAnalysisFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  articleId: string
): ContentAnalysis | undefined {
  return queryClient.getQueryData<ContentAnalysis>(articlesAnalysisKeys.byArticleId(articleId));
}

/**
 * Set article analysis in cache
 * Also syncs to Zustand for backward compatibility.
 */
export function setArticleAnalysisInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  articleId: string,
  analysis: ContentAnalysis
): void {
  queryClient.setQueryData(articlesAnalysisKeys.byArticleId(articleId), analysis);
}

/**
 * Remove article analysis from cache by article ID
 * Also syncs to Zustand for backward compatibility.
 */
export function removeArticleAnalysisFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  articleId: string
): void {
  queryClient.removeQueries({
    queryKey: articlesAnalysisKeys.byArticleId(articleId),
  });
}

/**
 * Get semantic terms from cache with optional filtering
 * Replaces: getStores().articlesAnalysis.getState().getSemanticTermsFromStore(articleId, params)
 */
export function getSemanticTermsFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  articleId: string,
  params?: {
    isHidden: boolean;
    searchTerm?: string | null;
    filters?: SemanticTermsFilters | null;
  }
): ContentAnalysisSemanticTerm[] {
  const analysis = getArticleAnalysisFromCache(queryClient, articleId);
  if (analysis == null) {
    return [];
  }

  if (params == null) {
    return analysis.semanticTerms;
  }

  const { isHidden, filters, searchTerm } = params;
  const filteredList: ContentAnalysisSemanticTerm[] = [];

  for (let i = 0; i < analysis.semanticTerms.length; i++) {
    const term = analysis.semanticTerms[i];
    if (term == null) {
      continue;
    }
    if (!includeByVisibility(term, isHidden)) {
      continue;
    }
    if (includeByFilter(term, filters) && includeBySearchTerm(term, searchTerm)) {
      filteredList.push(term);
    }
  }

  return filteredList;
}

/**
 * Toggle semantic term visibility in cache
 * Also syncs to Zustand for backward compatibility.
 * Replaces: getStores().articlesAnalysis.getState().toggleSemanticTermsVisibilityToStore(articleId, term, isHidden)
 */
export function toggleSemanticTermVisibilityInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  articleId: string,
  term: string,
  isHidden: boolean
): void {
  const analysis = getArticleAnalysisFromCache(queryClient, articleId);
  if (analysis == null) {
    return;
  }

  const updatedAnalysis: ContentAnalysis = {
    ...analysis,
    semanticTerms: analysis.semanticTerms.map(function (semanticTerm) {
      if (semanticTerm.term !== term) {
        return semanticTerm;
      }
      return { ...semanticTerm, isHidden };
    }),
  };

  queryClient.setQueryData(articlesAnalysisKeys.byArticleId(articleId), updatedAnalysis);
}

/**
 * Add a new semantic term to cache
 * Also syncs to Zustand for backward compatibility.
 * Replaces: getStores().articlesAnalysis.getState().createSemanticTermToStore(articleId, term)
 */
export function addSemanticTermToCache(
  queryClient: ReturnType<typeof useQueryClient>,
  articleId: string,
  term: ContentAnalysisSemanticTerm
): void {
  const analysis = getArticleAnalysisFromCache(queryClient, articleId);
  if (analysis == null) {
    return;
  }

  const updatedAnalysis: ContentAnalysis = {
    ...analysis,
    semanticTerms: [...analysis.semanticTerms, term],
  };

  queryClient.setQueryData(articlesAnalysisKeys.byArticleId(articleId), updatedAnalysis);
}

/**
 * Invalidate article analysis queries
 */
export function invalidateArticleAnalysis(
  queryClient: ReturnType<typeof useQueryClient>,
  articleId: string
): void {
  void queryClient.invalidateQueries({
    queryKey: articlesAnalysisKeys.byArticleId(articleId),
  });
}
